import { useState, useRef, useEffect } from 'react';
import { transcribeRecordedAudio } from '../lib/safariTranscription';
import { createAudioRecorder, assembleRecording, isAppleWebKit } from '../lib/recordingFormat';
import { voiceError } from '../lib/voiceDiagnostics';
import { acquireMicrophoneSession, createSpeechPauseDetector, microphoneRms } from '../lib/microphoneSession';

const releaseAudio = s => {
  clearInterval(s.timer); clearTimeout(s.finalizeTimer);
  s.stream?.getTracks().forEach(track => { try { track.stop(); } catch { /* Already interrupted. */ } });
  try { s.source?.disconnect(); } catch { /* Meter cleanup must not block upload. */ }
  try { s.silentSink?.disconnect(); } catch { /* Already disconnected. */ }
  if (s.context && s.context.state !== 'closed') {
    try {
      // Reuse a suspended context on Safari instead of repeatedly destroying
      // and recreating its native audio session between short recordings.
      const operation = s.keepContext ? s.context.suspend() : s.context.close();
      operation?.catch(() => {});
    } catch { /* Already closed/interrupted by iOS. */ }
  }
  s.releaseSession?.(); s.releaseSession = null;
  if (s.stream && !s.released) {
    s.released = true;
  }
  s.context = null;
};

export function useRecorder({ onTranscript, onError }) {
  const [status, setStatus] = useState('idle'), [seconds, setSeconds] = useState(0), [level, setLevel] = useState(0);
  const state = useRef({}), warmContext = useRef(null); const callbacks = useRef({ onTranscript, onError });
  callbacks.current = { onTranscript, onError };
  useEffect(() => {
    if (status !== 'saved') return;
    const timer = setTimeout(() => setStatus('idle'), 3500);
    return () => clearTimeout(timer);
  }, [status]);
  useEffect(() => () => {
    const s = state.current; s.cancelled = true; s.uploadController?.abort();
    if (s.recorder?.state === 'recording') s.recorder.stop();
    releaseAudio(s); // On unmount this capture is cancelled, never uploaded.
    const context = warmContext.current; warmContext.current = null;
    if (context && context.state !== 'closed') { try { context.close().catch(() => {}); } catch {} }
  }, []);

  const fail = (s, error) => {
    if (s.cancelled || s.failed || state.current !== s) return;
    s.failed = true; releaseAudio(s); setLevel(0); setStatus('idle');
    if (typeof error.code !== 'string') {
      error = s.phase === 'saving'
        ? voiceError('SAVE_FAILED', 'Your words were transcribed, but saving could not be confirmed. Please try again.')
        : voiceError('CAPTURE_FAILED', 'The recording could not be completed. Please try again.');
    }
    callbacks.current.onError(error);
  };
  const stop = () => {
    const s = state.current;
    if (s.stopRequested || s.recorder?.state !== 'recording') return;
    s.stopRequested = true; s.phase = 'finalizing'; clearInterval(s.timer); setStatus('transcribing');
    // Keep tracks and AudioContext ALIVE until dataavailable and stop finish.
    // Stopping them here can truncate the final AAC/MP4 data on Safari.
    s.finalizeTimer = setTimeout(() => fail(s, voiceError('RECORDER_FINALIZE_TIMEOUT', 'The recording could not finish. Please tap the microphone and try again.')), 15000);
    try { s.recorder.stop(); }
    catch { fail(s, voiceError('RECORDER_STOP_FAILED', 'The recording could not finish. Please try again.')); }
  };

  const start = async () => {
    if (state.current.starting || state.current.recorder?.state === 'recording') return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      callbacks.current.onError(voiceError('RECORDING_UNSUPPORTED', 'Voice recording isn’t supported in this browser. You can type instead.')); return;
    }
    const s = { id: `voice-${Date.now().toString(36)}`, starting: true, chunks: [], peak: 0, phase: 'permission', appleWebKit: isAppleWebKit() };
    state.current = s; setStatus('starting'); setSeconds(0); setLevel(0);
    s.releaseSession = acquireMicrophoneSession(s.appleWebKit);
    // Start/resume metering in the tap gesture. Metering is optional and must
    // never block MediaRecorder if an iOS audio context remains suspended.
    const Context = window.AudioContext || window.webkitAudioContext;
    if (Context) {
      try {
        s.context = s.appleWebKit && warmContext.current?.state !== 'closed' && warmContext.current
          ? warmContext.current : new Context();
        s.keepContext = s.appleWebKit && typeof s.context.suspend === 'function';
        if (s.keepContext) warmContext.current = s.context;
        s.context.resume().catch(() => {});
      }
      catch { s.context = null; }
    }
    try {
      s.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (s.cancelled) { releaseAudio(s); return; }
      const { recorder, requestedMime } = createAudioRecorder(s.stream);
      s.recorder = recorder; s.requestedMime = requestedMime;
      recorder.ondataavailable = event => {
        if (s.cancelled || s.failed) return;
        if (event.data?.size > 0) s.chunks.push(event.data);
      };
      recorder.onerror = event => fail(s, voiceError(event.error?.name || 'RECORDER_ENCODER_ERROR', 'The recording could not be completed. Please try again.'));
      recorder.onstop = async () => {
        clearTimeout(s.finalizeTimer); clearInterval(s.timer);
        if (s.cancelled || s.failed || s.finished) return;
        s.finished = true; setStatus('transcribing');
        try {
          // The stop event follows the FINAL dataavailable event. Only now is
          // the complete file safe to assemble and the microphone safe to stop.
          const audio = await assembleRecording(s.chunks, recorder.mimeType);
          releaseAudio(s); setLevel(0);
          if (s.cancelled) return;
          if (audio.blob.size < 100) throw voiceError('NO_AUDIO_DATA', 'No sound was recorded. Please check your microphone and try again.');
          if (!audio.extension) throw voiceError('AUDIO_FORMAT_UNSUPPORTED', 'I couldn’t read this recording. Please try again.');
          s.uploadController = new AbortController();
          s.phase = 'transcribing';
          const text = await transcribeRecordedAudio(audio, { appleWebKit: s.appleWebKit, signal: s.uploadController.signal, captureId: s.id, onPhase: phase => { s.phase = phase; } });
          if (s.cancelled) return;
          s.phase = 'saving';
          await callbacks.current.onTranscript(text);
          if (!s.cancelled && state.current === s) setStatus('saved');
        } catch (error) { fail(s, error); }
      };
      // A single complete final file avoids Safari's empty short-timeslice
      // fragments. Auto-stop behavior remains the existing 1.8s speech pause.
      recorder.start(); s.phase = 'recording'; s.starting = false; s.started = Date.now(); s.lastSpeech = s.started;
      if (s.appleWebKit) s.speechPause = createSpeechPauseDetector(s.started);
      if (s.context) {
        try {
          if (s.context.state !== 'running') s.context.resume().catch(() => {});
          s.analyser = s.context.createAnalyser(); s.analyser.fftSize = 512;
          s.source = s.context.createMediaStreamSource(s.stream); s.source.connect(s.analyser);
          s.buffer = typeof s.analyser.getFloatTimeDomainData === 'function' ? new Float32Array(s.analyser.fftSize) : new Uint8Array(s.analyser.fftSize);
          if (s.appleWebKit && s.context.createGain && s.context.destination) {
            s.silentSink = s.context.createGain(); s.silentSink.gain.value = 0;
            s.analyser.connect(s.silentSink); s.silentSink.connect(s.context.destination);
          }
        } catch { s.analyser = null; }
      }
      setStatus('listening');
      s.timer = setInterval(() => {
        const now = Date.now(); setSeconds(Math.floor((now - s.started) / 1000));
        if (s.analyser && s.context?.state === 'running') {
          const rms = microphoneRms(s.analyser, s.buffer);
          if (rms !== null) {
            s.peak = Math.max(s.peak, rms); setLevel(Math.min(1, rms * 8));
            if (s.speechPause) { if (s.speechPause.update(rms, now)) stop('silence'); }
            else {
              if (rms > 0.018) { s.spoke = true; s.lastSpeech = now; }
              if (s.spoke && now - s.lastSpeech > 1800 && now - s.started > 2000) stop('silence');
            }
          }
        }
        if (now - s.started > 60000) stop('limit');
      }, 100);
    } catch (error) {
      const message = error.name === 'NotAllowedError' || error.name === 'SecurityError' ? 'Microphone access is blocked. Allow it in your browser or iPhone settings, or type instead.' : error.name === 'NotFoundError' ? 'No microphone was found. Connect one or type instead.' : error.name === 'NotReadableError' ? 'The microphone is busy or unavailable. Close other recording apps and try again.' : 'Could not start recording. Please try again.';
      fail(s, voiceError(error.name || 'MICROPHONE_START_FAILED', message));
    } finally { s.starting = false; }
  };
  return { status, seconds, level, start, stop };
}